<template>
  <v-container fluid>
    <v-card class="mb-4">
      <v-card-title>
        Reporte de Ventas por Ticket
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.dateFrom"
              type="date"
              label="Fecha inicial"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.dateTo"
              type="date"
              label="Fecha final"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.sellerId"
              :items="sellers"
              item-text="name"
              item-value="id"
              label="Vendedor"
              clearable
              outlined
              dense
            />
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mt-2">
          <v-btn color="primary" class="mr-2 mb-2" :loading="loading" @click="runReport">
            <v-icon left>mdi-play</v-icon>
            Ejecutar
          </v-btn>
          <v-btn text class="mr-2 mb-2" @click="clearFilters">
            <v-icon left>mdi-filter-remove-outline</v-icon>
            Limpiar filtros
          </v-btn>
          <v-btn color="success" class="mb-2" :loading="downloading" :disabled="rows.length === 0" @click="downloadReport">
            <v-icon left>mdi-file-excel</v-icon>
            Descargar Excel
          </v-btn>
          <v-spacer></v-spacer>
          <div class="caption grey--text mb-2">
            {{ meta.totalCount }} tickets | {{ formatNumber(meta.totals.productsCount) }} productos/unidades | {{ formatCurrency(meta.totals.totalSales) }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>
        Tickets de venta
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        :expanded.sync="expanded"
        item-key="receiptId"
        show-expand
        class="elevation-1"
        dense
        :footer-props="{ itemsPerPageText: 'Filas por pagina', itemsPerPageOptions: [10, 25, 50, 100] }"
      >
        <template #[`item.dateNew`]="{ item }">
          {{ formatDateTime(item.dateNew) }}
        </template>
        <template #[`item.productsCount`]="{ item }">
          {{ formatNumber(item.productsCount) }}
        </template>
        <template #[`item.total`]="{ item }">
          {{ formatCurrency(item.total) }}
        </template>
        <template #[`item.sellerName`]="{ item }">
          {{ item.sellerName || 'Sin vendedor' }}
        </template>
        <template #expanded-item="{ headers: tableHeaders, item }">
          <td :colspan="tableHeaders.length" class="pa-0">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th class="text-right">Unidades</th>
                  <th class="text-right">Precio</th>
                  <th class="text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in item.lines" :key="`${item.receiptId}-${line.line}`">
                  <td>{{ line.productName }}</td>
                  <td class="text-right">{{ formatNumber(line.units) }}</td>
                  <td class="text-right">{{ formatCurrency(line.price) }}</td>
                  <td class="text-right">{{ formatCurrency(line.subtotal) }}</td>
                </tr>
                <tr v-if="!item.lines || item.lines.length === 0">
                  <td colspan="4" class="text-center grey--text">
                    Sin detalle de productos para este ticket.
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </td>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import * as salesService from '@/services/salesService';

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultFilters = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    dateFrom: formatInputDate(firstDay),
    dateTo: formatInputDate(today),
    sellerId: null,
  };
};

export default {
  name: 'SalesTicketsReportView',
  data() {
    return {
      loading: false,
      downloading: false,
      sellers: [],
      rows: [],
      expanded: [],
      meta: {
        totalCount: 0,
        totals: {
          ticketsCount: 0,
          productsCount: 0,
          totalSales: 0,
        },
      },
      filters: defaultFilters(),
      headers: [
        { text: 'Fecha', value: 'dateNew' },
        { text: 'Ticket', value: 'ticketNumber' },
        { text: 'Cliente', value: 'customerName' },
        { text: 'Vendedor', value: 'sellerName' },
        { text: 'Productos/Unidades', value: 'productsCount', align: 'end' },
        { text: 'Total', value: 'total', align: 'end' },
        { text: '', value: 'data-table-expand' },
      ],
    };
  },
  async mounted() {
    await this.loadSellers();
    await this.runReport();
  },
  methods: {
    cleanParams() {
      const params = { ...this.filters };
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === undefined || params[key] === null) {
          delete params[key];
        }
      });
      return params;
    },
    async loadSellers() {
      try {
        this.sellers = await salesService.fetchSalesSellers();
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar vendedores.', type: 'error' });
      }
    },
    async runReport() {
      if (!this.filters.dateFrom || !this.filters.dateTo) {
        this.$emit('show-alert', { message: 'Selecciona fecha inicial y final.', type: 'warning' });
        return;
      }

      this.loading = true;
      try {
        const response = await salesService.fetchSalesTicketsReportData(this.cleanParams());
        this.rows = response.rows || [];
        this.expanded = [];
        this.meta = response.meta || {
          totalCount: 0,
          totals: { ticketsCount: 0, productsCount: 0, totalSales: 0 },
        };
      } catch (error) {
        this.rows = [];
        this.expanded = [];
        this.meta = {
          totalCount: 0,
          totals: { ticketsCount: 0, productsCount: 0, totalSales: 0 },
        };
        this.$emit('show-alert', { message: 'Error al ejecutar el reporte por ticket.', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.filters = defaultFilters();
      this.runReport();
    },
    async downloadReport() {
      this.downloading = true;
      try {
        const blob = await salesService.downloadSalesTicketsReport(this.cleanParams());
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `reporte_ventas_por_ticket_${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al descargar el reporte por ticket.', type: 'error' });
      } finally {
        this.downloading = false;
      }
    },
    formatCurrency(value) {
      return `$${Number(value || 0).toFixed(2)}`;
    },
    formatDateTime(value) {
      if (!value) return '';
      return new Date(value).toLocaleString('es-MX');
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('es-MX');
    },
  },
};
</script>
